import React, { createContext } from 'react';
import { render } from 'react-dom';
import { Route, Switch, Redirect } from 'react-router-dom';
import { ConnectedRouter } from 'react-router-redux';
import { Provider } from 'react-redux';
import 'font-awesome/css/font-awesome.min.css';
import 'hint.css/hint.min.css';
import 'driver.js/dist/driver.css';

import { changeProduct } from '@xanadu/ui-shared/modules/application/actions';
import { initialiseUser } from '@xanadu/ui-shared/modules/userSession/actions';

import { dispatch as gtmDispatch } from '@xanadu/ui-shared/interfaces/googleTagManager';
import { userInformation as gtmUserInformation } from '@xanadu/ui-shared/interfaces/googleTagManager/actions';
import { initialiseDisplayPreferences } from '@xanadu/ui-shared/modules/user/actions';

import ScrollToTop from '@xanadu/ui-shared/components/ScrollToTop';
import { initializeUILayout } from '@xanadu/ui-shared/utils/uiLayout';

import './stylesheets/overrideDriverJs.scss';
import './stylesheets/overrideCookiesManager.scss';


import ProductRoutes from './routers/productRoutes';
import Page from './routers/page';
import Colossus from './routers/colossus';
import PromoPage from './routers/promoPage';
import Promotions from './routers/promotions';
import Affiliates from './routers/affiliates';
import Reports from './routers/reports';
import Account from './routers/accountPage';
import Events from './routers/events';


import App from './containers/App';
import MBHelmet from './containers/MBHelmet';
import Homepage from './containers/homepage/Homepage';
import NotFound from './components/NotFound';

import { PRODUCTS, PRODUCT_ROUTES } from './constants';
import configureStore, { history } from './configureStore';

import './stylesheets/index.scss';


export const DispatchContext = createContext(null);

export function createApp() {
  let store = null;

  const bootStore = () => {
    store = configureStore();
    return store;
  };

  const initialise = (userData) => {
    store.dispatch(initialiseDisplayPreferences(userData));
    initializeUILayout(store);

    if (userData.username) {
      store.dispatch(initialiseUser(userData));
    }

    let storedProduct = PRODUCTS.exchange;

    // Build product paths string for the product routes
    const productKeys = Object.keys(PRODUCT_ROUTES).filter(key => key !== PRODUCTS.exchange);
    const productPaths = productKeys.reduce((acc, productKey, index) => {
      if (!acc) {
        return `(${PRODUCT_ROUTES[productKey]}`;
      }

      return `${acc}|${PRODUCT_ROUTES[productKey]}${index === productKeys.length - 1 ? ')' : ''}`;
    }, '');

    try {
      window.userReturning = localStorage.getItem('mb-user-returning');

      localStorage.setItem('mb-user-returning', true);
      storedProduct = localStorage.getItem('mb-product');

      const isValidProduct = productKeys.some(key => key === storedProduct);

      if (!isValidProduct) {
        storedProduct = PRODUCTS.exchange;
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('Error accessing localStorage. Most likely due to a form of incognito mode', e);
    }

    store.dispatch(changeProduct(storedProduct));

    gtmDispatch(gtmUserInformation(userData));

    render(
      <Provider store={store}>
        <DispatchContext.Provider value={store.dispatch}>
          <ConnectedRouter history={history}>
            <ScrollToTop>
              <MBHelmet />
              <App>
                <Switch>
                  <Redirect exact from="/account" to="/account/general-settings" />

                  <Route
                    path={productPaths}
                    component={ProductRoutes}
                  />

                  <Route
                    exact
                    path="/(bonuses|registration|login|reset-password)?"
                    component={Homepage}
                  />

                  <Route
                    path="/events"
                    component={Events}
                  />
                  <Route path="/account" component={Account} />
                  <Route path="/promo" component={PromoPage} />
                  <Route path="/promotions/:section?/:promoId?" component={Promotions} />
                  <Route path="/page" component={Page} />
                  <Route path="/reports" component={Reports} />

                  <Route path="/partners" component={Affiliates} />
                  <Route path="/colossus" component={Colossus} />

                  <Route component={NotFound} />
                </Switch>
              </App>
            </ScrollToTop>
          </ConnectedRouter>
        </DispatchContext.Provider>
      </Provider>,
      document.getElementById('root')
    );

    if (process.env.NODE_ENV !== 'production') {
      window.store = store;
    }
  };

  const nextApp = { bootStore, initialise };
  return nextApp;
}

export default { createApp };
